/**
 * Portfolios API — the sidebar's portfolio LIST, and the progress check behind
 * a big portfolio's refresh icon.
 *
 * The legacy call (apiservice.js:350-361, getPortfoliosName):
 *   GET /get-portfolio?list_all=true&user_email=<signed-in user>
 *     -> { own_portfolios: [...], team_portfolios: [...] }
 *
 * Exactly those two params. Legacy's function takes `(provider, datadogCSP)`
 * but never sends either, so the backend has never received a provider filter
 * on this call — none is added here, and the list does not change with the
 * Service Provider. (The same path, /get-portfolio, also LOADS a single
 * portfolio with different params; that is a separate endpoint, added next.)
 *
 * Both scopes arrive in one response, so the My / Org switch only picks a
 * field — it never refetches. Legacy refetches on every switch and every
 * provider change for an identical request; RTK Query's cache makes those
 * free.
 *
 * Request is identical for CCA and EIA: the product travels only as the
 * Appname header apiClient puts on every request.
 *
 * LIVE: ignores env.useMocks. The mock is a contract fixture for the tests.
 */
import { API_ENDPOINTS } from '@services/apiEndpoints';
import { baseApi } from '@services/baseApi';

const { PORTFOLIOS, JOB_STATUS } = API_ENDPOINTS.MANAGE_PORTFOLIO;

/**
 * Normalise the envelope: both fields always arrays.
 *
 * Fixes a legacy failure mode rather than reproducing it: legacy's
 * getPortfoliosName swallows errors and returns the AxiosError, whose
 * `data.own_portfolios` is undefined, so a failed refetch dispatches `undefined`
 * as the list and the sidebar silently empties. Here a failure is an RTK Query
 * error the component reports, and a success always has two arrays.
 *
 * @returns {{ own: object[], team: object[] }}
 */
export function normalizePortfolios(response) {
  return {
    own: Array.isArray(response?.own_portfolios) ? response.own_portfolios : [],
    team: Array.isArray(response?.team_portfolios) ? response.team_portfolios : [],
  };
}

/**
 * The progress figure out of a /job-status response, or null if there isn't a
 * usable one. Note the name: the LIST calls it `recommendation_percentage`, this
 * call `completion_percentage` — same number, two names.
 */
export function readCompletion(response) {
  const raw = response?.completion_percentage;
  // Number(null), Number('') and Number(false) are all 0 — accepting them would
  // silently drop a row to 0 %. Only a real number, or a numeric string, counts.
  if (typeof raw !== 'number' && !(typeof raw === 'string' && raw.trim() !== '')) return null;
  const value = Number(raw);
  // Negative is no progress figure at all (e.g. a -1 sentinel); above 100 is
  // still "done", so it is capped rather than shown as 150 %.
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.min(value, 100);
}

export const portfoliosApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * "How far along is this big portfolio?" — the sidebar's refresh icon.
     *
     * Legacy (apiservice.js:872-884): GET /job-status?portfolio_id=<_id>, once per
     * click, no polling; it reads `completion_percentage`, writes it into the row,
     * and at 100 refetches the list so the portfolio becomes openable.
     *
     * A mutation, although it is a GET: every click must reach the server. A
     * cached query would answer the second click with the first click's number.
     *
     * Takes the user too, purely to find the right cached list to update.
     */
    refreshPortfolioProgress: builder.mutation({
      query: ({ portfolioId }) => ({
        url: JOB_STATUS,
        params: { portfolio_id: portfolioId },
      }),
      transformResponse: readCompletion,
      async onQueryStarted({ portfolioId, userEmail }, { dispatch, queryFulfilled }) {
        let percent;
        try {
          ({ data: percent } = await queryFulfilled);
        } catch {
          return; // the component shows the failure on the row
        }
        // No usable number: leave the row as it was. Legacy writes `undefined`
        // into it, which then renders as 0 %.
        if (percent === null) return;

        dispatch(
          portfoliosApi.util.updateQueryData('getPortfolios', userEmail, (draft) => {
            for (const list of [draft.own, draft.team]) {
              const row = list.find((p) => p._id === portfolioId);
              if (row) row.recommendation_percentage = percent;
            }
          }),
        );

        // Finished: fetch the list again so the row picks up its new status and
        // can be opened — the same refetch legacy does at 100.
        if (percent >= 100) dispatch(baseApi.util.invalidateTags(['Portfolio']));
      },
    }),

    getPortfolios: builder.query({
      query: (userEmail) => ({
        url: PORTFOLIOS,
        params: { list_all: true, user_email: userEmail },
      }),
      transformResponse: normalizePortfolios,
      // Save / Update / Delete / Upload invalidate this tag when they land, which
      // is how legacy's "refetch the list after X" calls (six call sites) map
      // onto RTK Query.
      providesTags: ['Portfolio'],
    }),
  }),
});

export const { useGetPortfoliosQuery, useRefreshPortfolioProgressMutation } = portfoliosApi;
